import React, { createContext, useContext, useState } from 'react';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';

import {
  SidebarDialogContextValue,
  SidebarDialogProviderProps,
} from './SidebarDialogTypes';

const SidebarDialogContext = createContext<SidebarDialogContextValue | null>(
  null
);

export const SidebarDialogProvider: React.FC<SidebarDialogProviderProps> = ({
  children,
  open,
  onClose,
  hasFab = false,
  dialogTitle,
  dialogSubtitle,
  dialogLeftAdornment,
  dialogActions,
}) => {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Update sidebar state when mobile state changes
  React.useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const contextValue: SidebarDialogContextValue = {
    // Responsive behavior
    isMobile,
    sidebarOpen,
    setSidebarOpen,

    // Dialog state
    isOpen: open,
    onClose,
    hasFab,

    // Dialog content props (for fallback when no sidebar)
    dialogTitle,
    dialogSubtitle,
    dialogLeftAdornment,
    dialogActions,
  };

  return (
    <SidebarDialogContext.Provider value={contextValue}>
      {children}
    </SidebarDialogContext.Provider>
  );
};

export const useSidebarDialog = (): SidebarDialogContextValue => {
  const context = useContext(SidebarDialogContext);
  if (!context) {
    throw new Error(
      'useSidebarDialog must be used within a SidebarDialogProvider'
    );
  }
  return context;
};
